import { FC } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import useRequirementLabel from "@/pages/Requirement/hooks/useRequirementLabel";
import SText from "@/styles/SText";
import { Requirement } from "@/types/requirement";
import { dateFormat } from "@/utils/format";
import { plus } from "@/utils/calc";
import { numFormmater } from "@/pages/Requirement/util";

interface RequirementDescProps {
  detail?: Requirement;
}

const RequirementDesc: FC<RequirementDescProps> = ({
  detail
}) => {
  const { t } = useTranslation();
  const { getLabelShow } = useRequirementLabel();

  const product = detail?.product;

  const descOriginItems: Array<{ key: string; label: string; hidden?: boolean; children: React.ReactNode }> = [
    {
      key: "saleInnerNumber",
      label: t("sale.innerNumber"),
      hidden: getLabelShow("innerNumber"),
      children: <SText text={detail?.saleOrder?.innerNumber ?? ""} />,
    },
    {
      key: "shortName",
      label: t("sale.shortName"),
      hidden: getLabelShow("innerNumber"),
      children: <SText text={detail?.saleOrder?.shortName ?? ""} />,
    },
    {
      key: "saleNumber",
      hidden: getLabelShow("saleNumber"),
      label: t("sale.orderNumber"),
      children: <SText text={detail?.saleOrder?.number ?? ""} />,
    },
    {
      key: "saleCode",
      hidden: getLabelShow("customer"),
      label: t("sale.code"),
      children: <SText text={detail?.saleOrder?.code ?? ""} />,
    },
    {
      key: "saleShortName",
      hidden: getLabelShow("customer"),
      label: t("sale.shortName"),
      children: <SText text={detail?.saleOrder?.shortName ?? ""} />,
    },
    {
      key: "materialProgress",
      label: t("requirement.materialProgress"),
      hidden: getLabelShow("customer"),
      children: (
        <SText
          text={`${detail?.readyCount ?? 0} / ${detail?.itemCount ?? 0}`}
        />
      ),
    },
    {
      key: "estimatedCost",
      hidden: getLabelShow("price"),
      label: t("requirement.estimatedCost"),
      children: <SText text={detail?.estimatedCost ?? ""} />,
    },
    {
      key: "salePrice",
      hidden: getLabelShow("price"),
      label: t("sale.price"),
      // children: <SText text={saleItem?.price ?? ""} />,
      children: <SText text={""} />,
    },
    {
      key: "saleAmount",
      hidden: getLabelShow("price"),
      label: t("sale.totalPrice"),
      children: <SText text={numFormmater(Number(detail?.saleOrder?.totalAmount ?? ""))} />,
    },
    {
      key: "saleActualPrice",
      hidden: getLabelShow("price"),
      label: t("sale.actualPrice"),
      children: <SText text={numFormmater(Number(detail?.saleOrder?.totalRealAmount ?? ""))} />,
    },
    {
      key: "productCode",
      label: t("product.code"),
      hidden: getLabelShow("product") || !!!product,
      children: <SText text={product?.code ?? ""} />,
    },
    {
      key: "productName",
      hidden: getLabelShow("product") || !!!product,
      label: t("product.name"),
      children: <SText text={product?.name ?? ""} />,
    },
    {
      key: "productUom",
      hidden: getLabelShow("product") || !!!product,
      label: t("product.uom"),
      children: <SText text={product?.uom ?? ""} />,
    },
    {
      key: "productModal",
      hidden: getLabelShow("product") || !!!product,
      label: t("product.brand1"),
      children: <SText text={product?.model ?? ""} />,
    },
    {
      key: "mrd",
      label: t("requirement.mrd"),
      hidden: getLabelShow("mrd"),
      children: <SText text={detail?.mrd ? dateFormat(dayjs(detail?.mrd)) : ""} />,
    },
    {
      key: "eps",
      label: t("requirement.eps"),
      hidden: getLabelShow("eps"),
      children: <SText text={detail?.eps ? dateFormat(dayjs(detail?.eps)) : ""} />,
    },
    {
      key: 'reqDeliDate',
      label: t('sale.reqDeliDate'),
      hidden: getLabelShow("reqDeliDate"),
      children: <SText text={detail?.saleOrder?.reqDeliDate ? dateFormat(dayjs(detail?.saleOrder?.reqDeliDate)) : ''} />,
    },
    {
      key: 'preFinishDate',
      label: t('sale.preFinishDate'),
      hidden: getLabelShow("preFinishDate"),
      children: <SText text={detail?.saleOrder?.preFinishDate ? dateFormat(dayjs(detail?.saleOrder?.preFinishDate)) : ''} />,
    },
    {
      key: "saleQty",
      label: t("requirement.saleNum"),
      hidden: !!!detail?.saleOrder?.items?.[0]?.quantity,
      children: <SText text={
        numFormmater(Number(plus((detail?.saleOrder?.items?.[0]?.quantity || 0).toString(), (detail?.saleOrder?.items?.[0]?.bquantity || 0).toString()) || ''))
      } />,
    },
    {
      key: "prQty",
      label: t("requirement.prNum"),
      children: <SText text={numFormmater(Number(detail?.requireCount ?? ""))} />,
    },
    {
      key: "saleRemark",
      label: t("sale.reRemark"),
      children: <SText text={detail?.saleOrder?.items?.[0]?.remark ?? ""} />,
    },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "16px", // 设置项与项之间的间距
        width: "100%",
      }}
    >
      {descOriginItems
        .filter((item) => !item.hidden)
        .map((item) => (
          <div
            key={item.key}
            style={{
              display: "flex",
              alignItems: "center", // 垂直居中
              gap: "8px", // 标签和内容之间的间距
              flex: "1 1 auto", // 自动调整宽度
              minWidth: "200px", // 最小宽度，防止内容过窄
            }}
          >
            <div style={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
              {item.label}:
            </div>
            <div>{item.children}</div>
          </div>
        ))}
    </div>
  );
};

export default RequirementDesc;